import { Head, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import LogForm from "./_form"
import LogNoteForm from "./_log_note_form"

interface Customer {
  id: number
  display_name: string
}

interface Option {
  id: number
  name: string
}

interface LogData {
  header: string
  log: string
  notes: string
  log_date: string
  scheduled_date: string | null
  scheduled_notes: string
  log_type_id: number | null
  customer_location_id: number | null
  customer_user_id: number | null
  user_id: number | null
  urgent: boolean
}

interface Props {
  customer: Customer
  log: LogData
  log_types: Option[]
  locations: Option[]
  contacts: Option[]
  users: Option[]
  errors?: Record<string, string[]>
  type?: string
  employees?: Option[]
  admin_ids?: number[]
  sales_rep_id?: number | null
}

export default function New({
  customer,
  log,
  log_types,
  locations,
  contacts,
  users,
  errors,
  type,
  employees,
  admin_ids,
  sales_rep_id,
}: Props) {
  const isLogNote = type === "log_note"

  const { data, setData, post, processing, transform } = useForm({
    header: log.header,
    log: log.log,
    notes: log.notes,
    log_date: log.log_date,
    scheduled_date: log.scheduled_date,
    scheduled_notes: log.scheduled_notes,
    log_type_id: log.log_type_id,
    customer_location_id: log.customer_location_id,
    customer_user_id: log.customer_user_id,
    user_id: log.user_id,
    urgent: log.urgent,
  })

  transform((data) => ({ log: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/customers/${customer.id}/logs`)
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    { title: "Logs", href: `/customers/${customer.id}/logs` },
    {
      title: isLogNote ? "Log Note" : "New",
      href: `/customers/${customer.id}/logs/new`,
    },
  ]

  if (isLogNote) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title={`Add Log Note - ${customer.display_name}`} />

        <div className="p-6">
          <h1 className="mb-4 text-2xl font-semibold">Add Log Note</h1>

          <LogNoteForm
            customerId={customer.id}
            log={log}
            log_types={log_types}
            contacts={contacts}
            employees={employees || []}
            adminIds={admin_ids || []}
            salesRepId={sales_rep_id || null}
            errors={errors}
          />
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`New Log - ${customer.display_name}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">New Log</h1>

        <LogForm
          customerId={customer.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Create Log"
          log_types={log_types}
          locations={locations}
          contacts={contacts}
          users={users}
        />
      </div>
    </AppLayout>
  )
}
